import React from 'react';
import SuperApp from './pages/SuperApp';
import FrontDoor from './pages/FrontDoor';
import { Toaster } from './components/ui/sonner';
import CenterToastHost from './components/CenterToast';
import PreviewBanner from './components/PreviewBanner';
import AuthGateHost from './components/AuthGate';
import ContinueGate from './components/ContinueGate';
import RecoverGate from './components/RecoverGate';
import ClaimGate from './components/ClaimGate';
import InstallPromptHost from './components/InstallPromptHost';
import { OfferingsProvider } from './components/OfferingsProvider';
import { useSession } from './lib/session';
import { useFlag } from './lib/flags';
import { hasDeepLinkIntent, stashReturnTo, takeReturnTo } from './lib/urlIntent';
import { syncSignupConsent } from './lib/consent';

function App() {
  const session = useSession();
  // The wall is itself flagged. If magic-link email turns out to be slow or
  // unreliable, turning this off returns the app to browse-freely rather than
  // leaving members stuck at a door they cannot open.
  const wallOn = useFlag('login_wall');

  // A shared listing link should survive the round trip through sign-in.
  React.useEffect(() => {
    if (!session) stashReturnTo();
  }, [session]);

  React.useEffect(() => {
    if (!session) return;
    // Copy the consent captured at sign-up into the ledger. No-op until P7.
    syncSignupConsent();
    const returnTo = takeReturnTo();
    if (returnTo && window.location.search !== returnTo) {
      window.history.replaceState(window.history.state, '', window.location.pathname + returnTo);
    }
  }, [session]);

  // ContinueGate and ClaimGate ARE the sign-in path for anyone arriving from an
  // email, so the wall must let those loads through to them.
  const showWall = wallOn && !session && !hasDeepLinkIntent;

  return (
    <OfferingsProvider>
    <div className="h-screen flex flex-col bg-background">
      <PreviewBanner />
      <div className="flex-1 min-h-0">
        {showWall ? <FrontDoor /> : <SuperApp />}
      </div>
      {/* Validation errors — sonner, top-center */}
      <Toaster position="top-center" />
      {/* Everyday confirmations (copy link, coming-soon taps, etc.) — centered pill with a cute icon */}
      <CenterToastHost />
      {/* Sign-up / log-in gate, opened on demand at the point of commitment */}
      <AuthGateHost />
      {/* Verify-on-click magic-link interstitial (?continue=1#th=<token_hash>&em=<email>).
          Mounted before ClaimGate: only a real tap exchanges the token_hash for a
          session, then hands off to ClaimGate via the same pending-claim flag. */}
      <ContinueGate />
      {/* Verify-on-click password reset (?recover=1#th=<token_hash>&em=<email>) —
          same mail-scanner-safe pattern as ContinueGate. Exchanges the recovery
          token_hash for a short-lived session, sets the new password, then signs
          out locally so the user proves the new password works by logging in. */}
      <RecoverGate />
      {/* QR claim entry point (?claim=<token> or #claim=<token>) — mounted after
          AuthGateHost so its requireAuth() has a live gate to open on first run */}
      <ClaimGate />
      {/* "Add to home screen", after the first sign-in. Mounted last so it
          overlays everything, and flagged off by default (install_prompt). */}
      <InstallPromptHost />
    </div>
    </OfferingsProvider>
  );
}

export default App;
